import { useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../_redux/homeworks/HomeworksAction";
import {
  ExportButton,
  copy,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  print,
  redirectToPages
} from "../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { useHomeworksUIContext } from "./HomeworksUIContext";
import { HomeworksFilter } from "./homeworks-filter/HomeworksFilter";
import { HomeworksTable } from "./homeworks-table/HomeworksTable";

export function HomeworksCard() {
  // Homeworks Card UI Context
  const homeworksUIContext = useHomeworksUIContext();
  const homeworksUIProps = useMemo(() => {
    return {
      queryParams: homeworksUIContext.queryParams
    };
  }, [homeworksUIContext]);

  // Getting curret state of Homeworks list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.homeworks }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized } = getAuthorities(authorities);

  // Homeworks Redux state
  const dispatch = useDispatch();

  useEffect(() => {
    return () => dispatch(actions.cleanStudentsState());
  }, [dispatch]);

  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Ödev Listesini Excele Aktarır */
  const exportHomeworksToExcel = () => {
    dispatch(actions.getAllHomeworks(homeworksUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [30, 25, 30, 25, 20, 50, 50, 20];
      const columns = ["Öğretmen", "Sınıf", "Ders", "Ödev Türü", "Tarih", "Ödev Tanımı", "Ek", "Durum"];
      entities.forEach(entity => rows.push([
        entity.teacher,
        entity.section,
        entity.course,
        entity.homeworkTypeName,
        entity.createDate,
        entity.definition,
        entity.attachmentName,
        entity.isEvaluated ? "Değerlendirildi" : "Değerlendirilmedi"
      ]));
      const filename = homeworksUIProps.queryParams.term + " - " + (homeworksUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Ödev Listesi";
      const sheetname = "Ödev Listesi"
      exportToExcel({ columns, rows, widths, filename, sheetname });
    });
  }

  /** Belli Bir Ödev Listesini PDF e Aktarır */
  const exportHomeworksToPDF = () => {
    dispatch(actions.getAllHomeworks(homeworksUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "Öğretmen",
          rows: entities.map(entity => entity.teacher)
        },
        {
          header: "Sınıf",
          rows: entities.map(entity => entity.section)
        },
        {
          header: "Ders",
          rows: entities.map(entity => entity.course)
        },
        {
          header: "Ödev Türü",
          rows: entities.map(entity => entity.homeworkTypeName)
        },
        {
          header: "Tarih",
          rows: entities.map(entity => entity.createDate)
        },
        {
          header: "Ödev Tanımı",
          rows: entities.map(entity => entity.definition)
        },
        {
          header: "Ek",
          rows: entities.map(entity => entity.attachmentName)
        },
        {
          header: "Durum",
          rows: entities.map(entity => entity.isEvaluated ? "Değerlendirildi" : "Değerlendirilmedi")
        }
      ];
      
      const filename = homeworksUIProps.queryParams.term + " - " + (homeworksUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Ödev Listesi";
      exportToPDF(views, filename);
    });
  }

  /** Belli Bir Ödev Listesini Yazdırma Penceresini Açar */
  const printHomeworks = () => {
    dispatch(actions.getAllHomeworks(homeworksUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];
      const header = homeworksUIProps.queryParams.term + " - " + (homeworksUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Ödev Listesi";
      const properties = ["Öğretmen", "Sınıf", "Ders", "Ödev Türü", "Tarih", "Ödev Tanımı", "Ek", "Durum"];

      entities.forEach(entity => {
        data.push({          
          "Öğretmen": entity.teacher,
          "Sınıf": entity.section,
          "Ders": entity.course,
          "Ödev Türü": entity.homeworkTypeName,
          "Tarih": entity.createDate,
          "Ödev Tanımı": entity.definition,
          "Ek": entity.attachmentName ?? "",
          "Durum": entity.isEvaluated ? "Değerlendirildi" : "Değerlendirilmedi"
        });
      });

      print({ data, properties, header });
    });
  }

  /** Belli Bir Ödev Listesini Panoya Kopyalar */
  const copyHomeworks = () => {
    dispatch(actions.getAllHomeworks(homeworksUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const columns = ["Öğretmen", "Sınıf", "Ders", "Ödev Türü", "Tarih", "Ödev Tanımı", "Ek", "Durum"];
      const header = homeworksUIProps.queryParams.term + " - " + (homeworksUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Ödev Listesi";
      entities.forEach(entity => rows.push([
        entity.teacher,
        entity.section,
        entity.course,
        entity.homeworkTypeName,
        entity.createDate,
        entity.definition,
        entity.attachmentName ?? "",
        entity.isEvaluated ? "Değerlendirildi" : "Değerlendirilmedi"
      ]));
      copy({ header, columns, rows });
    });
  }

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title="Ödev Listesi">
        {
          isExportationAuthorized &&
          <CardHeaderToolbar>
            <ExportButton
              exportToExcel={exportHomeworksToExcel}
              exportToPDF={exportHomeworksToPDF}
              print={printHomeworks}
              copy={copyHomeworks}
            />
          </CardHeaderToolbar>
        }
      </CardHeader>
      <CardBody>
        <HomeworksFilter />
        <HomeworksTable />
      </CardBody>
    </Card>
  );
}